import type { ComponentWithValues } from './ComponentWithValuesDocuments';
import type { StackConfigComponent } from 'common/stack/StackConfigDocuments';

export const replaceVariablesInString = ({
  value,
  variables,
}: {
  value: string;
  variables: { [key: string]: string | number | boolean };
}) =>
  value.replace(/\${([^{]+)}/g, (match, key) => {
    if (key in variables) return `${variables[key]}`;
    return match;
  });

export const listComponentEnvConfigPaths = (env: StackConfigComponent['env']) => {
  if (!env) return [];

  const list = [];
  if (env.include) {
    if (typeof env.include === 'string') list.push(env.include);
    else list.push(...env.include);
  }
  if (env.exclude) {
    if (typeof env.exclude === 'string') list.push(env.exclude);
    else list.push(...env.exclude);
  }

  return list;
};

/**
 * Convert component parameterGroups into environmentVariables.
 * Will include a variable for the "enabled" state for each parameter group.
 */
export const convertParameterGroupsToEnvs = (parameterGroups: ComponentWithValues['parameterGroups']) =>
  parameterGroups.flatMap((parameterGroup) => [
    // Enabled state for the parameterGroup
    {
      identifier: parameterGroup.identifier,
      value: `${parameterGroup.enabled}`,
    },

    // All the parameters inside of the parameterGroup
    ...parameterGroup.parameters.map((parameter) => ({
      identifier: parameter.identifier,
      value: `${parameter.value}`,
    })),
  ]);

/**
 * Returns built-in variables for a component.
 * These can be used in variable substitution in component configs.
 */
export const listComponentBuiltInVariables = (componentWithValues: Pick<ComponentWithValues, 'identifier'>) => {
  return {
    DIPLOI_NAMESPACE: componentWithValues.identifier,
  };
};

export interface ListComponentEnvironmentVariablesParams {
  component: Pick<ComponentWithValues, 'stackComponentID' | 'identifier' | 'parameterGroups' | 'environmentVariables'> & {
    config: Pick<NonNullable<ComponentWithValues['config']>, 'environmentVariables'> | null;
  };
  importedVariables?: { stackComponentID: number; identifier: string; value: string }[];
  includeBuiltInVariables?: boolean;
  includeUserDefinedVariables?: boolean;
}

/**
 * List all environment variables for a component.
 * Can include duplicates, but will always be in the order of importance.
 */
export const listComponentEnvironmentVariables = ({
  component,
  importedVariables: importedEnvs = [],
  includeBuiltInVariables = false,
  includeUserDefinedVariables = false,
}: ListComponentEnvironmentVariablesParams) => {
  const parameterEnvs = convertParameterGroupsToEnvs(component.parameterGroups).map((env) => ({
    stackComponentID: component.stackComponentID,
    identifier: env.identifier,
    value: env.value,
  }));

  const envs = (component.config?.environmentVariables || []).map((env) => ({
    stackComponentID: component.stackComponentID,
    identifier: env.identifier,
    value: `${env.defaultValue}`,
  }));

  const userDefinedEnvs = component.environmentVariables.map((env) => ({
    stackComponentID: component.stackComponentID,
    identifier: env.identifier,
    value: env.value,
  }));

  const builtInEnvs = Object.entries(listComponentBuiltInVariables(component)).map(([identifier, value]) => ({
    stackComponentID: component.stackComponentID,
    identifier,
    value,
  }));

  return [
    ...parameterEnvs,
    ...envs,
    ...importedEnvs,
    ...(includeUserDefinedVariables ? userDefinedEnvs : []),
    ...(includeBuiltInVariables ? builtInEnvs : []),
  ];
};
